"use client";

import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Card,
  CardMedia,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import CustomButton from "./CustomButton";
import RbtThumbnail from "../assets/Rbt_Thumbnail.jpg";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import axios from "axios";
import { useNavigate } from "react-router-dom";

interface RbtDetails {
  rbtCode: string;
  rbtName: string;
  artistName: string;
  price: number;
  imgPath: string;
  playPath: string;
}

interface Props {
  rbtDetails: RbtDetails;
  categoryId?: string;
  phoneNumber?: string;
}

const RbtDetailsPage: React.FC<Props> = ({
  rbtDetails,
  categoryId,
  phoneNumber,
}) => {
  const [isGiftOpen, setIsGiftOpen] = useState(false);
  const [friendNumber, setFriendNumber] = useState("");
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: "buy" | "gift" | null;
  }>({ open: false, type: null });

  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const navigate = useNavigate();

  const { countryCode, msisdn } = useSelector(
    (state: RootState) => state.phone
  );

  let audio = null;

const fetchAudioAndPlay = async () => {
  const audio = audioRef.current;
  if (!audio) return;

  if (isPlaying) {
    audio.pause();
    setIsPlaying(false);
    return;
  }

  try {
    const username = countryCode + msisdn;
    const baseUrl = process.env.REACT_APP_API_URL;

    const url = `${baseUrl}/Content/handleMusicPlayer?msisdn=${username}&service=CRBT&interface=W&langId=1&playPath=${rbtDetails.playPath}`;

    const response = await axios.get(url, {
      responseType: "blob",
    });

    const audioBlob = new Blob([response.data], { type: "audio/wav" });
    const audioURL = URL.createObjectURL(audioBlob);

    audio.src = audioURL;
    await audio.play();
    setIsPlaying(true);

  } catch (err) {
    console.error("Audio play error:", err);
    alert("Unable to play audio");
  }
};

  const handleBuy = () => {
    setConfirmDialog({ open: true, type: "buy" });
  };

  const handleGiftClick = () => {
    setIsGiftOpen(!isGiftOpen);
  };

  const handleGiftConfirm = () => {
    if (!friendNumber) {
      alert("Please enter a valid friend number.");
      return;
    }
    setConfirmDialog({ open: true, type: "gift" });
  };

  const handleConfirmAction = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const service = process.env.REACT_APP_SERVICE;
      const interfaces = process.env.REACT_APP_INTERFACES;
      const langId = process.env.REACT_APP_LANGID;

      const msisdnFinal =
        confirmDialog.type === "gift"
          ? `${countryCode}${friendNumber}`
          : `${countryCode}${msisdn}`;

      const rbtCode = rbtDetails?.rbtCode;

      let res: any;
      if (confirmDialog.type === "gift") {
        res = await axios.post(
          `${process.env.REACT_APP_API_URL}/Purchase/GiftRBT?msisdn=${
            countryCode + msisdn
          }&service=${process.env.REACT_APP_SERVICE}&interface=${
            process.env.REACT_APP_INTERFACES
          }&langId=${
            process.env.REACT_APP_LANGID
          }&rbtCode=${rbtCode}&planId=1&subType=O&fmsisdn=${msisdnFinal}`
        );

      } else {
        res = await axios.post(
          `${baseUrl}/Purchase/AddRBT?msisdn=${msisdnFinal}&service=${service}&interface=${interfaces}&langId=${langId}&planId=1&subType=P&rbtCode=${rbtCode}`
        );
      }
      navigate(`/flowtone/myTones`);

      setConfirmDialog({ open: false, type: null });

      alert(
        confirmDialog.type === "buy"
          ? "Tone purchased successfully!"
          : "Tone gifted successfully!"
      );
    } catch (error) {
      console.error("Purchase/Gift Error:", error);
      alert("Something went wrong!");
    }
  };


  useEffect(() => {
    if (!audioRef.current) return;

    const audio = audioRef.current;

    audio.onerror = () => {
      console.log("AUDIO ERROR:", audio.error);
    };

    audio.oncanplay = () => {
      console.log("Audio can play");
    };
  });

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.addEventListener("error", (e) =>
        console.log("Audio error", e)
      );
      audioRef.current.addEventListener("canplay", () =>
        console.log("Audio loaded")
      );
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleEnded = () => setIsPlaying(false);
    audio.addEventListener("ended", handleEnded);
    return () => {
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid size={5}>
          <Card
            sx={{
              // borderRadius: 2,
              boxShadow: "none",
              border: "1px solid #cfc2c2ff",
              overflow: "hidden",
              textAlign: "center",
              width: "260px",
            }}
          >
            <CardMedia
              component="img"
              // height="200"
              // width={"180"}
              image={RbtThumbnail}
              alt={rbtDetails?.rbtName}
            />
            <Box sx={{ p: 1 }}>
              <IconButton
                onClick={fetchAudioAndPlay}
                // onClick={handlePlayPause}
                color="primary"
                size="large"
                sx={{ border: "1px solid #cfc2c2ff" }}
              >
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>

              <audio
                ref={audioRef}
                preload="auto"
                playsInline
                onEnded={() => setIsPlaying(false)}
                onError={() =>
                  console.log(
                    "Audio error — file missing at:",
                    audioRef.current?.src
                  )
                }
              />
            </Box>
          </Card>
        </Grid>

        <Grid size={7}>
          <Typography variant="h4" fontWeight="bold">
            {rbtDetails?.rbtName}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {rbtDetails?.artistName}
          </Typography>

          <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1 }}>
            <CustomButton
              text={`Buy (${categoryId === "1784" ? "0.00" : "$"})`}
              sx={{ width: "375px" }}
              onClick={handleBuy}
            />
            <CustomButton
              text={`Gift (${categoryId === "1784" ? "0.00" : "$"})`}
              sx={{ width: "375px" }}
              onClick={handleGiftClick}
            />

            {isGiftOpen && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body1">{countryCode}</Typography>
                <TextField
                  type="number"
                  value={friendNumber}
                  onChange={(e) => setFriendNumber(e.target.value)}
                  placeholder="Enter friend number"
                  size="small"
                  sx={{ flex: 0.7 }}
                />
                <CustomButton text="Submit" onClick={handleGiftConfirm} />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: null })}
      >
        <DialogTitle>
          {confirmDialog.type === "buy" ? "Confirm Purchase" : "Confirm Gift"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to{" "}
            {confirmDialog.type === "buy" ? "purchase" : "gift"}{" "}
            <strong>{rbtDetails?.rbtName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, type: null })}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RbtDetailsPage;
